import React from 'react';
import { ArrowLeft, CheckCircle2, RefreshCw, Trash2 } from 'lucide-react';
import { tg } from '../../telegram';
import { formatNumber } from '../../utils/format';

export interface ConfirmItem {
  label: string;
  value: string | number;
  sub?: string;
  badge?: string;
}

interface ConfirmViewProps {
  title: string;
  description?: string;
  items: ConfirmItem[];
  note?: string;
  onConfirm: () => void;
  onBack: () => void;
  onDeleteItem?: (index: number, item: ConfirmItem) => void;
  isSubmitting: boolean;
  isDisabled?: boolean;
  confirmText?: string;
}

export const ConfirmView: React.FC<ConfirmViewProps> = ({
  title,
  description = 'Vui lòng kiểm tra lại thông tin chi tiết trước khi xác nhận lưu vào hệ thống:',
  items,
  note,
  onConfirm,
  onBack,
  onDeleteItem,
  isSubmitting,
  isDisabled = false,
  confirmText = 'Xác Nhận & Ghi Dữ Liệu',
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={onBack}
          disabled={isSubmitting}
          className="stepper-btn"
          style={{ width: 36, height: 36 }}
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800 }}>{title}</h2>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Bước xác nhận dữ liệu</span>
        </div>
      </div>

      <div className="card" style={{ padding: 14 }}>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
          {description}
        </p>

        {/* Item List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '50vh', overflowY: 'auto' }}>
          {items.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
              Không còn mục nào trong danh sách. Vui lòng quay lại để thêm dữ liệu.
            </div>
          ) : (
            items.map((it, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--bg-surface-subtle)',
                  gap: 12,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', wordBreak: 'break-word' }}>
                    {it.label}
                  </div>
                  {it.sub && (
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      {it.sub}
                    </div>
                  )}
                  {it.badge && (
                    <span className="badge badge-neutral" style={{ fontSize: 10, marginTop: 4 }}>
                      {it.badge}
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, textAlign: 'right' }}>
                  <span className="num" style={{ fontSize: 16, fontWeight: 800, color: 'var(--primary-accent)' }}>
                    {typeof it.value === 'number' ? formatNumber(it.value) : it.value}
                  </span>

                  {onDeleteItem && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        tg.haptic.impact('medium');
                        onDeleteItem(idx, it);
                      }}
                      disabled={isSubmitting}
                      className="stepper-btn"
                      style={{
                        width: 34,
                        height: 34,
                        color: 'var(--color-danger)',
                        background: 'rgba(239, 68, 68, 0.08)',
                        borderRadius: 'var(--radius-sm)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                      title="Xóa dòng này"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {note && (
          <div style={{ marginTop: 12, padding: '8px 12px', backgroundColor: 'var(--bg-surface-subtle)', borderRadius: 6, fontSize: 12 }}>
            <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Ghi chú: </span>
            <span>{note}</span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button
          onClick={() => {
            tg.haptic.impact('heavy');
            onConfirm();
          }}
          disabled={isDisabled || isSubmitting}
          className="btn btn-primary btn-full"
          style={{ fontSize: 16, minHeight: 52 }}
        >
          {isSubmitting ? (
            <>
              <RefreshCw size={18} className="animate-spin" /> Đang xử lý...
            </>
          ) : (
            <>
              <CheckCircle2 size={18} /> {confirmText}
            </>
          )}
        </button>

        <button
          onClick={onBack}
          disabled={isSubmitting}
          className="btn btn-secondary btn-full"
          style={{ minHeight: 44 }}
        >
          Quay lại chỉnh sửa
        </button>
      </div>
    </div>
  );
};
